import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CheckOutlined, InfoCircleOutlined } from '@ant-design/icons';
import styles from './auth-layout.module.scss';

/** Chip nhỏ nền trắng viền hồng trong dải đầu thẻ, vd. <AuthChip icon={<SafetyOutlined />}>. */
export const AuthChip = ({ icon, children }: { icon?: ReactNode; children: ReactNode }) => (
  <span className={styles.chip}>
    {icon}
    {children}
  </span>
);

export interface AuthSwitchProps {
  /** Câu phụ chữ thường, vd. "Đã có tài khoản?". */
  prompt: ReactNode;
  to: string;
  /** Động từ cụ thể, vd. "Đăng nhập", "Xem cách được cấp quyền". */
  label: ReactNode;
  state?: unknown;
}

/** Một dòng "câu phụ + link" cho dải cuối thẻ (`cardFooter`). Xếp nhiều dòng thì cách nhau 4px. */
export const AuthSwitch = ({ prompt, to, label, state }: AuthSwitchProps) => (
  <p className={styles.switchLine}>
    {prompt}{' '}
    <Link to={to} state={state}>
      {label}
    </Link>
  </p>
);

/** Dòng ghi chú có icon (mặc định InfoCircle), 13/20 màu chữ phụ, cách trên 16. */
export const AuthNote = ({ icon, children }: { icon?: ReactNode; children: ReactNode }) => (
  <p className={styles.note}>
    {icon ?? <InfoCircleOutlined aria-hidden="true" />}
    <span>{children}</span>
  </p>
);

/** Huy hiệu tròn 56px nền xanh nhạt + dấu tích — màn "Hoàn tất" / "Tài khoản đã tạo". */
export const AuthSuccessBadge = () => (
  <div className={styles.successBadge} aria-hidden="true">
    <CheckOutlined />
  </div>
);

export interface AuthStepItem {
  title: ReactNode;
  description?: ReactNode;
}

/** Danh sách <ol> các bước, số trong vòng tròn 28px (trang Yêu cầu quyền, màn đăng ký xong). */
export const AuthSteps = ({ items, className }: { items: AuthStepItem[]; className?: string }) => (
  <ol className={className ? `${styles.steps} ${className}` : styles.steps}>
    {items.map((item, index) => (
      <li key={index} className={styles.step}>
        <span className={styles.stepNum} aria-hidden="true">
          {index + 1}
        </span>
        <span>
          <span className={styles.stepTitle}>{item.title}</span>
          {item.description ? <span className={styles.stepDesc}>{item.description}</span> : null}
        </span>
      </li>
    ))}
  </ol>
);

/**
 * 3 bước "làm sao để vào được trang quản trị" (đặc tả §4, phương án B). Dùng chung cho
 * /request-access và màn "Tài khoản đã tạo" của /register để hai nơi nói cùng một câu.
 */
export const ADMIN_ACCESS_STEPS: AuthStepItem[] = [
  {
    title: 'Có một tài khoản HP Media.',
    description:
      'Dùng email bạn đang đăng nhập app HP Media. Chưa có tài khoản? Đăng ký trong app, hoặc nhờ Admin tạo trực tiếp.',
  },
  {
    title: 'Gửi yêu cầu cho Admin.',
    description:
      'Báo email tài khoản và vai trò cần cấp (thường là MOD) cho Admin hoặc quản lý trực tiếp.',
  },
  {
    title: 'Đăng nhập khi đã được cấp quyền.',
    description:
      'Admin cấp vai trò trong mục Quản lý người dùng. Sau đó bạn đăng nhập trang này bằng email và mật khẩu của tài khoản đó.',
  },
];
